import { supabase } from './supabaseService';
import { Appointment } from '../types/multiTenant';

/**
 * Appointment Service
 * Handles all appointment-related operations with Supabase
 * Appointments are automatically filtered by organization_id via RLS
 */
// Supabase PostgREST default page size. Sem .range(), o servidor corta em 1000
// linhas — independente do .limit() do cliente. Quando a organização passa de
// 1000 appointments, os mais recentes (que ficam no final do ORDER BY ASC) somem.
// Ver: docs/marketing/auditoria-clinafy-trafego-pago.md → seção root-cause 2026-05-11
const APPOINTMENTS_PAGE_SIZE = 1000;
const APPOINTMENTS_MAX_PAGES = 10; // safeguard contra loop infinito (até 10k rows)

class AppointmentService {
  /**
   * List all appointments for an organization.
   *
   * Paginate explicitly to bypass the PostgREST 1000-row default cap.
   * Without this, orgs com mais de 1000 appointments perdem os mais recentes
   * silenciosamente — bug que causou perda visual de consultas da Marli.
   */
  async listAppointments(
    organizationId: string,
    filters?: {
      clientId?: string;
      assignedTo?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<Appointment[]> {
    try {
      const allRows: any[] = [];
      let pageIndex = 0;
      let hasMore = true;

      while (hasMore && pageIndex < APPOINTMENTS_MAX_PAGES) {
        const from = pageIndex * APPOINTMENTS_PAGE_SIZE;
        const to = from + APPOINTMENTS_PAGE_SIZE - 1;

        let query = supabase
          .from('appointments')
          .select(`
            *,
            client:clients(id, full_name),
            assigned_user:users!appointments_assigned_to_fkey(id, full_name)
          `)
          .eq('organization_id', organizationId)
          .eq('deleted', false);

        if (filters?.clientId) query = query.eq('client_id', filters.clientId);
        if (filters?.assignedTo) query = query.eq('assigned_to', filters.assignedTo);
        if (filters?.status) query = query.eq('status', filters.status);
        if (filters?.startDate) query = query.gte('start_time', filters.startDate);
        if (filters?.endDate) query = query.lte('start_time', filters.endDate);

        // Tiebreaker `id` é OBRIGATÓRIO: `start_time` não é único — várias
        // consultas no mesmo horário existem. Sem o segundo `.order()`, o
        // PostgreSQL pode reordenar timestamps empatados entre 2 requests
        // paginados, resultando em rows duplicadas em uma página e ausentes
        // na outra. Esse era o mesmo bug "agendamentos somem" em outra forma.
        query = query
          .order('start_time', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to);

        const { data, error } = await query;

        if (error) {
          console.error('Error listing appointments (page', pageIndex, '):', error);
          // Se já carregamos algumas páginas, devolve parcial em vez de perder tudo
          if (allRows.length > 0) break;
          return [];
        }

        const rows = data || [];
        allRows.push(...rows);
        hasMore = rows.length === APPOINTMENTS_PAGE_SIZE;
        pageIndex++;
      }

      // Safeguard: se atingiu o teto de páginas mas ainda havia mais, avisar.
      // Antes saía silenciosamente em 10.000 rows — mesmo padrão do bug 1000-row.
      if (pageIndex >= APPOINTMENTS_MAX_PAGES && hasMore) {
        console.warn(
          `[listAppointments] Atingido teto de ${APPOINTMENTS_MAX_PAGES} páginas ` +
          `(${APPOINTMENTS_MAX_PAGES * APPOINTMENTS_PAGE_SIZE} rows) para org ${organizationId}. ` +
          `Aumentar APPOINTMENTS_MAX_PAGES ou filtrar por janela temporal.`
        );
      }

      return allRows.map(this.transformAppointment);
    } catch (error) {
      console.error('Error in listAppointments:', error);
      return [];
    }
  }

  /**
   * Get appointment by ID
   */
  async getAppointmentById(appointmentId: string): Promise<Appointment | null> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          client:clients(id, full_name),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .eq('id', appointmentId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching appointment:', error);
        return null;
      }

      return this.transformAppointment(data);
    } catch (error) {
      console.error('Error in getAppointmentById:', error);
      return null;
    }
  }

  /**
   * Get appointments by client
   */
  async getAppointmentsByClient(clientId: string): Promise<Appointment[]> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          client:clients(id, full_name),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .eq('client_id', clientId)
        .eq('deleted', false)
        .order('start_time', { ascending: false });

      if (error) {
        console.error('Error fetching appointments by client:', error);
        return [];
      }

      return (data || []).map(this.transformAppointment);
    } catch (error) {
      console.error('Error in getAppointmentsByClient:', error);
      return [];
    }
  }

  /**
   * Get appointments by date range
   */
  async getAppointmentsByDateRange(
    organizationId: string,
    startDate: string,
    endDate: string
  ): Promise<Appointment[]> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          client:clients(id, full_name),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .gte('start_time', startDate)
        .lte('start_time', endDate)
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching appointments by date range:', error);
        return [];
      }

      return (data || []).map(this.transformAppointment);
    } catch (error) {
      console.error('Error in getAppointmentsByDateRange:', error);
      return [];
    }
  }

  /**
   * Create new appointment
   */
  async createAppointment(data: {
    organizationId: string;
    clientId: string;
    assignedTo?: string;
    startTime: string;
    endTime: string;
    durationMinutes?: number;
    status?: string;
    attendanceStatus?: string;
    title?: string;
    notes?: string;
    location?: string;
    videoCallUrl?: string;
    price?: number;
    currency?: string;
    reminderConfig?: any;
    isRecurring?: boolean;
    recurrenceRule?: string;
    parentAppointmentId?: string;
    createdBy: string;
  }): Promise<Appointment | null> {
    try {
      const { data: appointmentData, error } = await supabase
        .from('appointments')
        .insert({
          organization_id: data.organizationId,
          client_id: data.clientId,
          assigned_to: data.assignedTo || null,
          start_time: data.startTime,
          end_time: data.endTime,
          duration_minutes: data.durationMinutes || 60,
          status: data.status || 'scheduled',
          attendance_status: data.attendanceStatus,
          title: data.title,
          notes: data.notes,
          location: data.location || 'office',
          video_call_url: data.videoCallUrl,
          price: data.price,
          currency: data.currency || 'BRL',
          reminder_config: data.reminderConfig,
          is_recurring: data.isRecurring || false,
          recurrence_rule: data.recurrenceRule,
          parent_appointment_id: data.parentAppointmentId,
          created_by: data.createdBy,
        })
        .select(`
          *,
          client:clients(id, full_name),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .single();

      if (error) {
        console.error('Error creating appointment:', error);
        throw new Error(error.message || 'Erro ao salvar agendamento no banco');
      }

      return this.transformAppointment(appointmentData);
    } catch (error) {
      console.error('Error in createAppointment:', error);
      throw error;
    }
  }

  /**
   * Update appointment
   */
  async updateAppointment(
    appointmentId: string,
    updates: Partial<Appointment>
  ): Promise<Appointment | null> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          client_id: updates.clientId,
          assigned_to: updates.assignedTo === '' ? null : updates.assignedTo,
          start_time: updates.startTime,
          end_time: updates.endTime,
          duration_minutes: updates.durationMinutes,
          status: updates.status,
          attendance_status: updates.attendanceStatus,
          title: updates.title,
          notes: updates.notes,
          location: updates.location,
          video_call_url: updates.videoCallUrl,
          price: updates.price,
          currency: updates.currency,
          reminder_sent: updates.reminderSent,
          reminder_sent_at: updates.reminderSentAt,
          reminder_config: updates.reminderConfig,
        })
        .eq('id', appointmentId)
        .select(`
          *,
          client:clients(id, full_name),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .single();

      if (error) {
        console.error('Error updating appointment:', error);
        throw new Error(error.message || 'Erro ao atualizar agendamento no banco');
      }

      return this.transformAppointment(data);
    } catch (error) {
      console.error('Error in updateAppointment:', error);
      throw error;
    }
  }

  /**
   * Delete appointment (soft delete)
   */
  async deleteAppointment(appointmentId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          deleted: true,
          status: 'canceled',
        })
        .eq('id', appointmentId)
        .select();

      if (error) {
        console.error('Error deleting appointment:', error);
        throw new Error(error.message || 'Erro ao cancelar agendamento no banco');
      }

      // Return true only if at least one row was affected
      return data && data.length > 0;
    } catch (error) {
      console.error('Error in deleteAppointment:', error);
      throw error;
    }
  }

  /**
   * Mark appointment as completed
   */
  async completeAppointment(appointmentId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          status: 'completed',
          attendance_status: 'present',
        })
        .eq('id', appointmentId)
        .select();

      if (error) {
        console.error('Error completing appointment:', error);
        return false;
      }

      // Return true only if at least one row was affected
      return data && data.length > 0;
    } catch (error) {
      console.error('Error in completeAppointment:', error);
      return false;
    }
  }

  /**
   * Mark appointment as no-show
   */
  async markAsNoShow(appointmentId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          status: 'no_show',
          attendance_status: 'absent',
        })
        .eq('id', appointmentId)
        .select();

      if (error) {
        console.error('Error marking as no-show:', error);
        return false;
      }

      // Return true only if at least one row was affected
      return data && data.length > 0;
    } catch (error) {
      console.error('Error in markAsNoShow:', error);
      return false;
    }
  }

  /**
   * Get confirmation URL for an appointment
   */
  getConfirmationUrl(confirmationToken: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/confirm/${confirmationToken}`;
  }

  /**
   * Send confirmation request for an appointment
   * This marks the confirmation as sent and returns the confirmation URL
   */
  async sendConfirmation(appointmentId: string): Promise<{ success: boolean; url?: string }> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          confirmation_sent_at: new Date().toISOString(),
        })
        .eq('id', appointmentId)
        .select('confirmation_token')
        .single();

      if (error) {
        console.error('Error sending confirmation:', error);
        return { success: false };
      }

      const url = this.getConfirmationUrl(data.confirmation_token);
      return { success: true, url };
    } catch (error) {
      console.error('Error in sendConfirmation:', error);
      return { success: false };
    }
  }

  /**
   * Manually confirm an appointment
   */
  async confirmAppointmentManually(appointmentId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          status: 'confirmed',
          confirmation_response: 'confirmed',
          confirmed_at: new Date().toISOString(),
          confirmation_method: 'manual',
        })
        .eq('id', appointmentId)
        .select();

      if (error) {
        console.error('Error confirming appointment manually:', error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error in confirmAppointmentManually:', error);
      return false;
    }
  }

  /**
   * Get appointments needing confirmation (upcoming, not yet confirmed)
   */
  async getAppointmentsNeedingConfirmation(organizationId: string): Promise<Appointment[]> {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          client:clients(id, full_name, phone, email),
          assigned_user:users!appointments_assigned_to_fkey(id, full_name)
        `)
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .in('status', ['scheduled'])
        .gte('start_time', now)
        .is('confirmation_response', null)
        .order('start_time', { ascending: true });

      if (error) {
        console.error('Error fetching appointments needing confirmation:', error);
        return [];
      }

      return (data || []).map(this.transformAppointment);
    } catch (error) {
      console.error('Error in getAppointmentsNeedingConfirmation:', error);
      return [];
    }
  }

  /**
   * Get appointment statistics
   */
  async getAppointmentStats(
    organizationId: string,
    startDate?: string,
    endDate?: string
  ): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byAttendance: Record<string, number>;
    upcomingCount: number;
  }> {
    try {
      const appointments = await this.listAppointments(organizationId, {
        startDate,
        endDate,
      });

      const now = new Date().toISOString();

      const stats = {
        total: appointments.length,
        byStatus: {} as Record<string, number>,
        byAttendance: {} as Record<string, number>,
        upcomingCount: 0,
      };

      appointments.forEach((appointment) => {
        // Count by status
        if (appointment.status) {
          stats.byStatus[appointment.status] =
            (stats.byStatus[appointment.status] || 0) + 1;
        }

        // Count by attendance
        if (appointment.attendanceStatus) {
          stats.byAttendance[appointment.attendanceStatus] =
            (stats.byAttendance[appointment.attendanceStatus] || 0) + 1;
        }

        // Count upcoming
        if (appointment.startTime && appointment.startTime > now) {
          stats.upcomingCount++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error in getAppointmentStats:', error);
      return {
        total: 0,
        byStatus: {},
        byAttendance: {},
        upcomingCount: 0,
      };
    }
  }

  /**
   * Transform database appointment to app format
   */
  private transformAppointment(data: any): Appointment {
    // Normalizar timezone - remover indicador UTC para tratar como horário local
    const normalizeTime = (timeStr: string): string => {
      if (!timeStr) return timeStr;
      // Remover Z, +00, +00:00 para evitar conversão de timezone
      return timeStr
        .replace(/Z$/, '')
        .replace(/\+00:00$/, '')
        .replace(/\+00$/, '')
        .replace(/ \+00$/, '');
    };

    return {
      id: data.id,
      organizationId: data.organization_id,
      clientId: data.client_id,
      assignedTo: data.assigned_to,
      startTime: normalizeTime(data.start_time),
      endTime: normalizeTime(data.end_time),
      durationMinutes: data.duration_minutes,
      status: data.status,
      attendanceStatus: data.attendance_status,
      title: data.title,
      notes: data.notes,
      location: data.location,
      videoCallUrl: data.video_call_url,
      price: data.price,
      currency: data.currency,
      reminderSent: data.reminder_sent,
      reminderSentAt: data.reminder_sent_at,
      reminderConfig: data.reminder_config,
      confirmationToken: data.confirmation_token,
      confirmationSentAt: data.confirmation_sent_at,
      confirmedAt: data.confirmed_at,
      confirmationResponse: data.confirmation_response,
      confirmationMethod: data.confirmation_method,
      isRecurring: data.is_recurring,
      recurrenceRule: data.recurrence_rule,
      parentAppointmentId: data.parent_appointment_id,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }
}

export const appointmentService = new AppointmentService();
export default appointmentService;
